import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../services/api";

import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Divider,
  Chip,
  Alert,
  IconButton,
  Avatar,
  Tooltip,
  Tabs,
  Tab,
  Skeleton,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Drawer,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  LinearProgress,
} from "@mui/material";
import { alpha, styled, useTheme } from "@mui/material/styles";
import { PageHeader, PageLayout } from "../../components/ui";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonIcon from "@mui/icons-material/Person";
import TimelineIcon from "@mui/icons-material/Timeline";
import InfoIcon from "@mui/icons-material/Info";
import WorkIcon from "@mui/icons-material/Work";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AssessmentIcon from "@mui/icons-material/Assessment";
import GroupIcon from "@mui/icons-material/Group";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SpeedIcon from "@mui/icons-material/Speed";
import ArticleIcon from "@mui/icons-material/Article";
import MenuIcon from "@mui/icons-material/Menu";

const Section = styled(Paper)(({ theme }) => ({
  borderRadius: "8px",
  boxShadow: "none",
  border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.14)}`,
  backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.96 : 0.84),
  overflow: "hidden",
}));

const StatusChip = styled(Chip, { shouldForwardProp: (prop) => prop !== "status" })(({ theme, status }) => {
  const s = String(status || "draft").toLowerCase();

  const map = {
    draft: { bg: alpha(theme.palette.grey[400], 0.18), fg: theme.palette.text.secondary },
    pending: { bg: alpha(theme.palette.warning.main, 0.16), fg: theme.palette.warning.main },
    not_started: { bg: alpha(theme.palette.warning.main, 0.16), fg: theme.palette.warning.main },
    in_progress: { bg: alpha(theme.palette.info.main, 0.16), fg: theme.palette.info.main },
    completed: { bg: alpha(theme.palette.success.main, 0.16), fg: theme.palette.success.main },
    submitted: { bg: alpha(theme.palette.success.main, 0.16), fg: theme.palette.success.main },
    cancelled: { bg: alpha(theme.palette.error.main, 0.16), fg: theme.palette.error.main },
  };

  const c = map[s] || map.draft;

  return {
    height: 26,
    borderRadius: "8px",
    fontWeight: 800,
    fontSize: "0.75rem",
    backgroundColor: c.bg,
    color: c.fg,
    border: `1px solid ${alpha(c.fg, 0.22)}`,
    textTransform: "capitalize",
  };
});

const MiniStat = ({ icon, label, value }) => (
  <Stack direction="row" spacing={1.25} alignItems="center">
    <Avatar
      sx={{
        width: 32,
        height: 32,
        borderRadius: "8px",
        bgcolor: (t) => alpha(t.palette.primary.main, 0.10),
        color: "primary.main",
      }}
    >
      {icon}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  </Stack>
);

/* ---------------------------------------
   Helpers
---------------------------------------- */
const safeMsg = (err, fallback = "Failed to load appraisal details") =>
  err?.response?.data?.message || err?.message || fallback;

const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const fmtDateTime = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const numOrNull = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const pickFirst = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return null;
};

const getSteps = (appraisal) => appraisal?.stepInstances || appraisal?.step_instances || [];

const isDoneStatus = (s) => ["completed", "submitted"].includes(String(s || "").toLowerCase());
const isInProgressStatus = (s) => ["in_progress"].includes(String(s || "").toLowerCase());
const isPendingStatus = (s) => ["pending", "not_started", "draft"].includes(String(s || "").toLowerCase());

const getCurrentStep = (appraisal, steps) => {
  if (!steps.length) return null;

  const currentOrder = appraisal?.current_step_order;
  if (currentOrder != null) {
    const byOrder = steps.find((x) => Number(x.step_order) === Number(currentOrder));
    if (byOrder) return byOrder;
  }

  const inProg = steps.find((x) => isInProgressStatus(x.status));
  if (inProg) return inProg;

  const firstPending = steps.find((x) => isPendingStatus(x.status));
  return firstPending || steps[0] || null;
};

const isSelfReviewStep = (employee, step) => {
  const empCode = employee?.employee_code;
  const stepUserCode = step?.user?.employee_code;
  if (empCode && stepUserCode) return String(empCode) === String(stepUserCode);

  if (employee?.id && step?.user?.id) return String(employee.id) === String(step.user.id);

  return false;
};

/* ----------------------------
   Q/A normalization
----------------------------- */
const normalizeApplicability = (v) => {
  if (v == null) return "YES";
  if (typeof v === "boolean") return v ? "YES" : "NO";
  const s = String(v).trim().toLowerCase();
  if (["1", "true", "yes", "y"].includes(s)) return "YES";
  if (["0", "false", "no", "n"].includes(s)) return "NO";
  return String(v).toUpperCase();
};

const getAnswerRating = (a) => a?.rating ?? a?.score ?? a?.value ?? a?.points ?? null;
const getAnswerApplicability = (a) => a?.applicability ?? a?.is_applicable ?? a?.isApplicable ?? null;
const getAnswerComment = (a) => a?.comment ?? a?.comments ?? a?.remark ?? a?.remarks ?? a?.note ?? a?.notes ?? "";

const normalizeQA = (item) => {
  const title =
    item?.question?.title ||
    item?.question?.name ||
    item?.question_title ||
    item?.title ||
    "Question";

  const rating = getAnswerRating(item);
  const app = normalizeApplicability(getAnswerApplicability(item) ?? item?.applicability);
  const comment = getAnswerComment(item) || "";

  const id = item?.id ?? `${title}-${rating ?? "x"}-${app}`;
  return { id, title, rating, app, comment };
};

const getStepQAItems = (step) => {
  const latest = step?.latest_submission || step?.latestSubmission || null;

  const fromSubmission = Array.isArray(latest?.answers) ? latest.answers : [];
  if (fromSubmission.length) return fromSubmission.map(normalizeQA);

  const fromStep = Array.isArray(step?.responses) ? step.responses : [];
  if (fromStep.length) return fromStep.map(normalizeQA);

  return [];
};

const getSubmissionSummary = (sub) =>
  sub?.summary_comment ??
  sub?.comment ??
  sub?.comments ??
  sub?.notes ??
  sub?.remark ??
  sub?.remarks ??
  "";

/* ---------------------------------------
   Main Component
---------------------------------------- */
export default function AppraisalShowPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [appraisal, setAppraisal] = useState(null);
  const [error, setError] = useState("");
  const [openSummary, setOpenSummary] = useState(false);

  const fetchDetails = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/performance/appraisals/${id}`);
      const payload = res?.data?.data ?? res?.data;
      setAppraisal(payload || null);
    } catch (err) {
      console.error("Failed to load appraisal details", err);
      setError(safeMsg(err));
      setAppraisal(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const employee = useMemo(() => appraisal?.employee || {}, [appraisal]);

  const steps = useMemo(() => {
    const list = getSteps(appraisal);
    return [...list].sort((a, b) => (Number(a.step_order) || 0) - (Number(b.step_order) || 0));
  }, [appraisal]);

  const workflowName = useMemo(
    () => appraisal?.workflowTemplate?.name || appraisal?.workflow_template?.name || "-",
    [appraisal]
  );

  const currentStep = useMemo(() => getCurrentStep(appraisal, steps), [appraisal, steps]);

  const stepStats = useMemo(() => {
    const total = steps.length || 0;
    const done = steps.filter((s) => isDoneStatus(s.status)).length;
    const inProgress = steps.filter((s) => isInProgressStatus(s.status)).length;
    const pending = Math.max(0, total - done - inProgress);
    const progress = total ? Math.round((done / total) * 100) : 0;
    return { total, done, inProgress, pending, progress };
  }, [steps]);

  const activeStepIndex = useMemo(() => {
    if (!currentStep) return 0;
    const idx = steps.findIndex((s) => String(s.id) === String(currentStep.id));
    return idx >= 0 ? idx : 0;
  }, [steps, currentStep]);

  // Previous score + delta (supports multiple API key names safely)
  const previousOverall = useMemo(() => {
    const a = appraisal || {};
    return pickFirst(
      a.previous_overall_rating,
      a.previous_overall,
      a.previousOverallRating,
      a.previousOverall,
      a.prev_overall_rating,
      a.prevOverallRating
    );
  }, [appraisal]);

  const previousPeriod = useMemo(() => {
    const a = appraisal || {};
    return pickFirst(a.previous_period, a.prev_period, a.previousPeriod, a.prevPeriod);
  }, [appraisal]);

  const ratingDelta = useMemo(() => {
    const cur = numOrNull(appraisal?.overall_rating);
    const prev = numOrNull(previousOverall);
    if (cur == null || prev == null) return null;
    return Math.round((cur - prev) * 100) / 100;
  }, [appraisal?.overall_rating, previousOverall]);

  const handleStartReview = () => {
    if (!appraisal || !currentStep) return;
    navigate(`/performance/appraisals/${id}/evaluate`);
  };

  const statusLabel = String(appraisal?.status || "draft").replaceAll("_", " ");

  const SummaryPanel = (
    <Stack spacing={1.5}>
      <Section>
        <Box sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Avatar
              sx={{
                width: 44,
                height: 44,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: "primary.main",
                fontWeight: 900,
              }}
            >
              {employee?.name?.charAt(0) || <PersonIcon />}
            </Avatar>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }} noWrap>
                {employee?.name || "-"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
                {employee?.employee_code || "-"} / {employee?.department || "-"}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          <Stack spacing={1}>
            <Box>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                Role
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                {employee?.actual_role || "-"}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                Manager
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                {employee?.manager?.name || "Not assigned"}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                Overall Rating
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 900, color: "primary.main" }}>
                {appraisal?.overall_rating ?? "-"}
              </Typography>
            </Box>

            {/* Previous score */}
            <Box>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                Previous Overall Rating
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                  {previousOverall ?? "-"}
                </Typography>

                {previousPeriod ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Prev Period: ${previousPeriod}`}
                    sx={{ borderRadius: "8px" }}
                  />
                ) : null}

                {ratingDelta != null ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Delta ${ratingDelta > 0 ? "+" : ""}${ratingDelta}`}
                    sx={{ borderRadius: "8px" }}
                  />
                ) : null}
              </Stack>
            </Box>
          </Stack>

          {currentStep ? (
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: "8px",
                border: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Avatar sx={{ width: 34, height: 34, bgcolor: "primary.main", color: "common.white" }}>
                  <VisibilityIcon fontSize="small" />
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                    {currentStep.step_name || `Step ${currentStep.step_order ?? "-"}`}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
                  {currentStep.user?.name || "Unassigned"} / {String(currentStep.status || "-").replaceAll("_", " ")}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleStartReview}
                  sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 800 }}
                >
                  {isInProgressStatus(currentStep.status) ? "Continue" : "Start"}
                </Button>
              </Stack>
            </Box>
          ) : null}
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
            <TimelineIcon sx={{ color: "text.secondary" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
              Workflow Steps
            </Typography>
          </Stack>

          {steps.length === 0 ? (
            <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: "8px" }}>
              No steps found for this appraisal.
            </Alert>
          ) : (
            <Stepper
              orientation="vertical"
              activeStep={activeStepIndex}
              sx={{
                "& .MuiStepConnector-line": { borderColor: alpha(theme.palette.divider, 0.25) },
              }}
            >
              {steps.map((s, idx) => {
                const active = currentStep && String(currentStep.id) === String(s.id);
                const done = isDoneStatus(s.status);

                const lastTs =
                  (s.latest_submission || s.latestSubmission)?.updated_at ||
                  (s.latest_submission || s.latestSubmission)?.submitted_at ||
                  (s.latest_submission || s.latestSubmission)?.created_at;

                return (
                  <Step key={s.id || `${s.step_order}-${idx}`} expanded>
                    <StepLabel
                      optional={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
                          <StatusChip status={s.status} label={String(s.status || "draft").replaceAll("_", " ")} />
                          {active ? (
                            <Chip size="small" variant="outlined" label="Current" sx={{ borderRadius: "8px" }} />
                          ) : null}
                        </Stack>
                      }
                      sx={{
                        "& .MuiStepLabel-label": { fontWeight: 900 },
                        "& .MuiStepIcon-root": {
                          color: done
                            ? theme.palette.success.main
                            : active
                            ? theme.palette.primary.main
                            : alpha(theme.palette.text.primary, 0.25),
                        },
                      }}
                    >
                      {s.step_name || `Step ${s.step_order ?? "-"}`}
                    </StepLabel>

                    <StepContent>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {s.user?.name || "Unassigned"} / {s.actor_type || "-"}
                      </Typography>

                      {lastTs ? (
                        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>
                          Last update: {fmtDateTime(lastTs)}
                        </Typography>
                      ) : null}
                    </StepContent>
                  </Step>
                );
              })}
            </Stepper>
          )}
        </Box>
      </Section>
    </Stack>
  );

  if (loading) {
    return (
      <PageLayout backgroundAccent compact maxWidth="xl">
          <Skeleton variant="rounded" height={64} sx={{ borderRadius: "8px", mb: 1.2 }} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "330px minmax(0, 1fr)" }, gap: 1.5 }}>
            <Skeleton variant="rounded" height={520} sx={{ borderRadius: "8px" }} />
            <Skeleton variant="rounded" height={520} sx={{ borderRadius: "8px" }} />
          </Box>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout backgroundAccent compact maxWidth="md">
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={fetchDetails} sx={{ borderRadius: "8px" }}>
                Retry
              </Button>
            }
            sx={{ borderRadius: "8px" }}
          >
            {error}
          </Alert>

          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/performance/appraisals/my")}
            sx={{ mt: 2, borderRadius: "8px" }}
          >
            Back to Appraisals
          </Button>
      </PageLayout>
    );
  }

  if (!appraisal) {
    return (
      <PageLayout backgroundAccent compact maxWidth="md">
          <Box sx={{ textAlign: "center", mt: 8 }}>
            <ArticleIcon sx={{ fontSize: 64, color: "text.secondary", opacity: 0.6, mb: 1.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 900 }} gutterBottom>
              Appraisal Not Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              The appraisal doesn&apos;t exist or you don&apos;t have access to it.
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/performance/appraisals/my")}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}
            >
              Back to Appraisals
            </Button>
          </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout backgroundAccent compact maxWidth="xl">
        <PageHeader
          compact
          overline={`Performance appraisal #${appraisal.id}`}
          title={`${employee?.name || "Employee"} appraisal`}
          subtitle={`${employee?.employee_code || "-"} / ${employee?.department || "-"} / ${workflowName}`}
          actions={[
            isMobile ? (
              <Tooltip key="summary" title="Summary">
                <IconButton
                  onClick={() => setOpenSummary(true)}
                  sx={{ borderRadius: "8px", border: `1px solid ${alpha(theme.palette.divider, 0.18)}` }}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>
            ) : null,
            <Button
              key="back"
              size="small"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/performance/appraisals/my")}
              sx={{ borderRadius: "8px" }}
            >
              Back
            </Button>,
            <Button
              key="refresh"
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchDetails}
              sx={{ borderRadius: "8px" }}
            >
              Refresh
            </Button>,
            <Button
              key="participants"
              size="small"
              variant="outlined"
              startIcon={<GroupIcon />}
              onClick={() => navigate(`/performance/appraisals/${id}/participants`)}
              sx={{ borderRadius: "8px" }}
            >
              Participants
            </Button>,
            currentStep ? (
              <Button
                key="evaluate"
                size="small"
                variant="contained"
                startIcon={<AssessmentIcon />}
                onClick={handleStartReview}
                sx={{ borderRadius: "8px" }}
              >
                {isInProgressStatus(currentStep.status) ? "Continue" : "Start"}{" "}
                {isSelfReviewStep(employee, currentStep) ? "Self Review" : "Evaluation"}
              </Button>
            ) : null,
          ].filter(Boolean)}
        />

        <Section>
          <Box sx={{ p: { xs: 1, md: 1.15 } }}>
            <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center">
              <StatusChip status={appraisal.status} label={statusLabel} />
              <Chip
                size="small"
                icon={<WorkIcon fontSize="small" />}
                label={workflowName}
                variant="outlined"
                sx={{ borderRadius: "8px" }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Progress ${stepStats.progress}%`}
                sx={{ borderRadius: "8px" }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Rating ${appraisal?.overall_rating ?? "-"}`}
                sx={{ borderRadius: "8px" }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Updated ${fmtDate(appraisal.updated_at)}`}
                sx={{ borderRadius: "8px" }}
              />
            </Stack>
          </Box>
        </Section>

        {/* Quick stats */}
        <Section sx={{ mt: 1 }}>
          <Box sx={{ p: { xs: 1, md: 1.15 } }}>
            <Stack spacing={1}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                      Progress
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {stepStats.done}/{stepStats.total} steps completed
                    </Typography>
                  </Stack>
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={stepStats.progress}
                      sx={{
                        height: 8,
                        borderRadius: 999,
                        bgcolor: alpha(theme.palette.primary.main, 0.10),
                        "& .MuiLinearProgress-bar": { borderRadius: 999 },
                      }}
                    />
                  </Box>
                </Box>

                <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="flex-end">
                  <MiniStat icon={<CheckCircleIcon fontSize="small" />} label="Done" value={stepStats.done} />
                  <MiniStat icon={<TimelineIcon fontSize="small" />} label="In Progress" value={stepStats.inProgress} />
                  <MiniStat icon={<PendingActionsIcon fontSize="small" />} label="Remaining" value={stepStats.pending} />
                  <MiniStat
                    icon={<CalendarTodayIcon fontSize="small" />}
                    label="Updated"
                    value={fmtDate(appraisal.updated_at)}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Section>

        {/* Main layout */}
        <Box
          sx={{
            mt: 1,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "330px minmax(0, 1fr)" },
            gap: 1,
            alignItems: "start",
          }}
        >
          {/* Left summary (desktop) */}
          {!isMobile ? (
            <Box sx={{ position: "sticky", top: theme.spacing(11) }}>
              {SummaryPanel}
            </Box>
          ) : null}

          {/* Right content */}
          <Section>
            <Box sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
              <Tabs
                value={tabValue}
                onChange={(_, v) => setTabValue(v)}
                variant={isMobile ? "scrollable" : "standard"}
                allowScrollButtonsMobile
                sx={{
                  px: 1,
                  minHeight: 42,
                  "& .MuiTab-root": {
                    minHeight: 42,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 800,
                  },
                  "& .MuiTabs-indicator": {
                    borderRadius: "8px",
                    height: 3,
                  },
                }}
              >
                <Tab label="Overview" icon={<SpeedIcon />} iconPosition="start" />
                <Tab label="Steps & Responses" icon={<TimelineIcon />} iconPosition="start" />
                <Tab label="Comments" icon={<ChatBubbleOutlineIcon />} iconPosition="start" />
              </Tabs>
            </Box>

            <Box sx={{ p: { xs: 1.25, sm: 1.5 } }}>
              {/* TAB 0: Overview */}
              {tabValue === 0 && (
                <Stack spacing={1.5}>
                  <Section>
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                        Appraisal Info
                      </Typography>
                      <Divider sx={{ my: 1.25 }} />

                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap">
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<CalendarTodayIcon fontSize="small" />}
                          label={`Created: ${fmtDate(appraisal.created_at)}`}
                          sx={{ borderRadius: "8px" }}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<CalendarTodayIcon fontSize="small" />}
                          label={`Updated: ${fmtDate(appraisal.updated_at)}`}
                          sx={{ borderRadius: "8px" }}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<SpeedIcon fontSize="small" />}
                          label={`Progress: ${stepStats.progress}%`}
                          sx={{ borderRadius: "8px" }}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Rating: ${appraisal?.overall_rating ?? "-"}`}
                          sx={{ borderRadius: "8px" }}
                        />

                        {/* Previous score in overview */}
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Previous: ${previousOverall ?? "-"}`}
                          sx={{ borderRadius: "8px" }}
                        />
                        {ratingDelta != null ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Delta ${ratingDelta > 0 ? "+" : ""}${ratingDelta}`}
                            sx={{ borderRadius: "8px" }}
                          />
                        ) : null}
                      </Stack>

                      {currentStep ? (
                        <Box
                          sx={{
                            mt: 2,
                            p: 1.25,
                            borderRadius: "8px",
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                            border: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
                          }}
                        >
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.5}
                            alignItems={{ xs: "stretch", sm: "center" }}
                          >
                            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                              <Avatar sx={{ bgcolor: "primary.main", color: "common.white", width: 36, height: 36 }}>
                                <VisibilityIcon fontSize="small" />
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                                  Current Step: {currentStep.step_name || `Step ${currentStep.step_order ?? "-"}`}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
                                  Assigned: {currentStep.user?.name || "Unassigned"} /{" "}
                                  {String(currentStep.status || "-").replaceAll("_", " ")}
                                </Typography>
                              </Box>
                            </Stack>

                            <Button
                              variant="contained"
                              onClick={handleStartReview}
                              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}
                            >
                              {isInProgressStatus(currentStep.status) ? "Continue" : "Start"}
                            </Button>
                          </Stack>
                        </Box>
                      ) : null}
                    </Box>
                  </Section>

                  {steps.some((s) => isPendingStatus(s.status)) ? (
                    <Section>
                      <Box sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                          Next Up
                        </Typography>

                        <Stack spacing={1}>
                          {steps
                            .filter((s) => isPendingStatus(s.status))
                            .slice(0, 4)
                            .map((s) => (
                              <Stack key={s.id || `${s.step_order}`} direction="row" spacing={1} alignItems="center">
                                <Chip
                                  size="small"
                                  label={s.step_order ?? "-"}
                                  sx={{
                                    borderRadius: "8px",
                                    fontWeight: 900,
                                    bgcolor: alpha(theme.palette.grey[500], 0.14),
                                  }}
                                />
                                <Typography variant="body2" sx={{ fontWeight: 800, minWidth: 0 }} noWrap>
                                  {s.step_name || `Step ${s.step_order ?? "-"}`}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary", ml: "auto" }} noWrap>
                                  {s.user?.name || "Unassigned"}
                                </Typography>
                              </Stack>
                            ))}
                        </Stack>
                      </Box>
                    </Section>
                  ) : null}
                </Stack>
              )}

              {/* TAB 1: Steps & Responses */}
              {tabValue === 1 && (
                <Stack spacing={1.25}>
                  {steps.length === 0 ? (
                    <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: "8px" }}>
                      No steps found.
                    </Alert>
                  ) : (
                    steps.map((step, idx) => {
                      const latest = step.latest_submission || step.latestSubmission || null;
                      const qaItems = getStepQAItems(step);
                      const summary = getSubmissionSummary(latest);
                      const isActive = currentStep && String(currentStep.id) === String(step.id);

                      return (
                        <Accordion
                          key={step.id || `${step.step_order}-${idx}`}
                          disableGutters
                          elevation={0}
                          sx={{
                            borderRadius: "8px",
                            border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
                            overflow: "hidden",
                            "&:before": { display: "none" },
                            ...(isActive && {
                              borderColor: alpha(theme.palette.primary.main, 0.35),
                              bgcolor: alpha(theme.palette.primary.main, 0.02),
                            }),
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, py: 1.25 }}>
                            <Box sx={{ width: "100%" }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }} noWrap>
                                    Step {step.step_order ?? "-"}: {step.step_name || "-"}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    {step.user?.name || "Unassigned"} / {step.actor_type || "-"}
                                    {isActive ? " / Current" : ""}
                                  </Typography>
                                </Box>

                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  flexWrap="wrap"
                                  justifyContent="flex-end"
                                >
                                  <StatusChip
                                    status={step.status}
                                    label={String(step.status || "draft").replaceAll("_", " ")}
                                  />
                                  {qaItems.length > 0 ? (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={`Q/A: ${qaItems.length}`}
                                      sx={{ borderRadius: "8px" }}
                                    />
                                  ) : null}
                                </Stack>
                              </Stack>
                            </Box>
                          </AccordionSummary>

                          <AccordionDetails sx={{ p: 2 }}>
                            <Stack spacing={1.5}>
                              {latest ? (
                                <Box
                                  sx={{
                                    p: 1.5,
                                    borderRadius: "8px",
                                    bgcolor: alpha(theme.palette.info.main, 0.05),
                                    border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
                                  }}
                                >
                                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={`Version: V${latest.version ?? "-"}`}
                                      sx={{ borderRadius: "8px" }}
                                    />
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      icon={<CalendarTodayIcon fontSize="small" />}
                                      label={`Submitted: ${fmtDateTime(
                                        latest.submitted_at || latest.completed_at || latest.updated_at || latest.created_at
                                      )}`}
                                      sx={{ borderRadius: "8px" }}
                                    />
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={`By: ${latest.submitted_by?.name || latest.submittedBy?.name || "-"}`}
                                      sx={{ borderRadius: "8px" }}
                                    />
                                    {latest?.is_draft ? (
                                      <Chip
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        label="Draft"
                                        sx={{ borderRadius: "8px" }}
                                      />
                                    ) : null}
                                  </Stack>

                                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 900 }}>
                                    Submission Summary
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: "pre-wrap" }}>
                                    {summary || "No summary provided."}
                                  </Typography>
                                </Box>
                              ) : null}

                              {qaItems.length > 0 ? (
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                                    Questions & Answers
                                  </Typography>

                                  <Stack spacing={1}>
                                    {qaItems.map((x) => (
                                      <Paper
                                        key={x.id}
                                        elevation={0}
                                        sx={{
                                          p: 1.25,
                                          borderRadius: "8px",
                                          border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
                                          bgcolor: alpha(theme.palette.background.default, 0.35),
                                        }}
                                      >
                                        <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                          {x.title}
                                        </Typography>

                                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.75 }}>
                                          <Chip
                                            size="small"
                                            variant="outlined"
                                            label={`Applicability: ${x.app ?? "-"}`}
                                            sx={{ borderRadius: "8px" }}
                                          />
                                          <Chip
                                            size="small"
                                            variant="outlined"
                                            label={`Rating: ${x.rating == null ? "-" : x.rating}`}
                                            sx={{ borderRadius: "8px" }}
                                          />
                                        </Stack>

                                        {x.comment ? (
                                          <Typography
                                            variant="body2"
                                            sx={{ mt: 1, color: "text.secondary", whiteSpace: "pre-wrap" }}
                                          >
                                            {x.comment}
                                          </Typography>
                                        ) : (
                                          <Typography variant="caption" sx={{ mt: 1, color: "text.secondary", display: "block" }}>
                                            No comment.
                                          </Typography>
                                        )}
                                      </Paper>
                                    ))}
                                  </Stack>
                                </Box>
                              ) : (
                                <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: "8px" }}>
                                  No per-question responses for this step yet.
                                </Alert>
                              )}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })
                  )}
                </Stack>
              )}

              {/* TAB 2: Comments */}
              {tabValue === 2 && (
                <Box sx={{ textAlign: "center", py: 5 }}>
                  <ChatBubbleOutlineIcon sx={{ fontSize: 64, color: "text.secondary", opacity: 0.55, mb: 1.5 }} />
                  <Typography variant="h6" sx={{ fontWeight: 900 }} gutterBottom>
                    Comments & Discussions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Comment functionality will be available in the next release.
                  </Typography>
                </Box>
              )}
            </Box>
          </Section>
        </Box>

        {/* Mobile Summary Drawer */}
        <Drawer
          anchor="left"
          open={openSummary}
          onClose={() => setOpenSummary(false)}
          PaperProps={{
            sx: {
              width: "92vw",
              maxWidth: 420,
              bgcolor: "background.default",
              p: 1.5,
              borderRight: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
            },
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
              Summary
            </Typography>
            <Button
              onClick={() => setOpenSummary(false)}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}
            >
              Close
            </Button>
          </Stack>

          {SummaryPanel}
          <Box sx={{ height: 12 }} />
        </Drawer>
    </PageLayout>
  );
}
